import { useCallback } from 'react'
import Icon from '@web-ui-kit/icon'
import ButtonIcon from '@web-ui-kit/icon-button'
import Tooltip from '@web-ui-kit/tooltip'
import Text from '@web-ui-kit/text'
import { v4 as uuidv4 } from 'uuid'
import PropTypes from 'prop-types'
import Destination from '@web-ui-kit/destination'
import * as Sc from './Sidebar.style'

const Sidebar = ({
  bottomOptions,
  children,
  className,
  logo,
  onClose,
  positionTooltip,
  topOptions,
}) => {
  const handleOptionsOnClick = useCallback((onClickOption) => {
    onClickOption()
    if (onClose) onClose()
  }, [])

  const renderOptions = (option) => {
    const {
      active,
      dataTestId,
      disabled,
      element,
      icon,
      id,
      onClick,
      text,
      tooltip,
    } = option || {}
    const renderCurrentDestination = () => {
      if (tooltip) {
        return (
          <Tooltip
            align='center'
            element={
              <Destination
                active={active}
                dataTestId={dataTestId}
                disabled={disabled}
                fullWidth
                icon={icon}
                id={id}
                onClick={() => handleOptionsOnClick(onClick)}
                text={text}
              />
            }
            id={`tooltip-${id}`}
            position={positionTooltip}
          >
            <Text variant='small'>{tooltip}</Text>
          </Tooltip>
        )
      }
      return (
        <Destination
          active={active}
          dataTestId={dataTestId}
          disabled={disabled}
          fullWidth
          icon={icon}
          id={id}
          onClick={() => handleOptionsOnClick(onClick)}
          text={text}
        />
      )
    }

    return <li key={id ?? uuidv4()}>{element ?? renderCurrentDestination()}</li>
  }

  return (
    <Sc.Container className={className} onClick={(e) => e.stopPropagation()}>
      {logo && <Sc.LogoContainer>{logo}</Sc.LogoContainer>}
      {onClose && (
        <Sc.CloseButtonIconContainer>
          <ButtonIcon icon={<Icon icon='close' />} onClick={onClose} />
        </Sc.CloseButtonIconContainer>
      )}
      <Sc.Content>
        {Boolean(topOptions.length) && (
          <Sc.OptionsList>
            {topOptions.map((option) => renderOptions(option))}
          </Sc.OptionsList>
        )}
        {children}
        {Boolean(bottomOptions.length) && (
          <Sc.OptionsList>
            <Sc.Divider />
            {bottomOptions.map((option) => renderOptions(option))}
          </Sc.OptionsList>
        )}
      </Sc.Content>
    </Sc.Container>
  )
}

Sidebar.defaultProps = {
  bottomOptions: [],
  children: null,
  className: null,
  logo: null,
  onClose: null,
  positionTooltip: 'left',
  topOptions: [],
}

Sidebar.propTypes = {
  bottomOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        active: PropTypes.bool,
        dataTestId: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
        icon: PropTypes.string.isRequired,
        id: PropTypes.string,
        onClick: PropTypes.func.isRequired,
        text: PropTypes.string.isRequired,
      }),
      PropTypes.shape({ element: PropTypes.node.isRequired }),
    ])
  ),
  children: PropTypes.node,
  className: PropTypes.string,
  logo: PropTypes.node,
  onClose: PropTypes.func,
  positionTooltip: PropTypes.oneOf(['bottom', 'left', 'right']),
  topOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        active: PropTypes.bool,
        dataTestId: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
        icon: PropTypes.string.isRequired,
        id: PropTypes.string,
        onClick: PropTypes.func.isRequired,
        text: PropTypes.string.isRequired,
      }),
      PropTypes.shape({ element: PropTypes.node.isRequired }),
    ])
  ),
}

export default Sidebar
